import { getLargeFormatProcessNames } from './largeFormatFinishing';

/**
 * Utility for converting drafts to quote items with full schema compatibility
 *
 * This module ensures that quotes generated from drafts have the EXACT same
 * schema as quotes generated directly from the calculator, including all
 * required fields for Printlogic submissions.
 */

/**
 * Helper: Get laminate type display name
 */
const getLaminateTypeName = (laminateTypeId) => {
  switch (laminateTypeId) {
    case 'matt_lam': return 'Matt';
    case 'gloss_lam': return 'Gloss';
    case 'soft_touch_lam': return 'Soft Touch';
    default: return '';
  }
};

/**
 * Helper: Get finishing material display name
 * Mirrors logic from useSmallFormatFormLogic.js
 */
const getFinishingMaterialDisplayName = (processId, materialId, allFinishingMaterials) => {
  if (!materialId) return '';
  if (processId === 'laminate' || processId === 'duplex-laminate') {
    return getLaminateTypeName(materialId);
  }

  const processSpecificMaterials = allFinishingMaterials?.[processId] || [];
  const foundMaterial = processSpecificMaterials.find(m => m.material_id === materialId);
  if (foundMaterial?.name) return foundMaterial.name;

  if (processId === 'encapsulate') {
    const defaults = [
      { material_id: '250matt', name: 'Matt (250 mic)' },
      { material_id: '250gloss', name: 'Gloss (250 mic)' }
    ];
    const defaultMat = defaults.find(m => m.material_id === materialId);
    if (defaultMat) return defaultMat.name;
  }

  return materialId;
};

/**
 * Helper: Map finish IDs to human-readable names with material types
 * Mirrors mapFinishes logic from useSmallFormatFormLogic.js:645-653
 */
const mapFinishes = (finishIds, finishingOptions, finishingProcesses, finishingMaterials) => {
  if (!Array.isArray(finishIds)) return [];

  return finishIds.map(id => {
    const process = finishingProcesses?.find(p => p.id === id);
    if (!process) return '';

    const optKey = (id === 'laminate' || id === 'duplex-laminate') ? 'laminateType' : 'material_type';
    const matId = finishingOptions?.[id]?.[optKey];
    const matName = matId ? getFinishingMaterialDisplayName(id, matId, finishingMaterials) : '';

    return matName ? `${process.name} (${matName})` : process.name;
  }).filter(Boolean);
};

/**
 * Helper: Generate NCR description string
 * Mirrors logic from useSmallFormatFormLogic.js:634-643
 */
const generateNcrDescription = (draft, paperStocks, standardSize = null) => {
  const { ncrType, sheetsPerSet, setsPerPad, width, height, paperSelections = [], hasWraparoundShield, hasLooseShield } = draft;

  const paperSelNames = paperSelections
    .map(id => paperStocks?.find(s => s.id === id)?.name || '')
    .filter(Boolean);

  const sizeStr = standardSize || `${width}x${height}mm`;
  let desc = ncrType === 'sets'
    ? `${sheetsPerSet}-part ${sizeStr} sets`
    : `${setsPerPad} sets of ${sheetsPerSet}-part ${sizeStr} per pad`;

  const extras = [];
  if (hasWraparoundShield) extras.push('wraparound shield');
  if (hasLooseShield) extras.push('loose shield');
  if (extras.length > 0) desc += ` with ${extras.join(' and ')}`;

  return { desc, paperSelNames };
};

/**
 * Build a small-format quote item from draft data
 * Mirrors createFullProductObject from useSmallFormatFormLogic.js:623-700
 */
const buildSmallFormatQuoteItem = (draft, options = {}) => {
  console.log('=== buildSmallFormatQuoteItem called ===');
  console.log('draft.jobType:', draft.jobType);
  console.log('draft.draftName:', draft.draftName);

  const {
    paperStocks = [],
    finishingProcesses = [],
    finishingMaterials = {},
    jobTypes = []
  } = options;

  console.log('Reference data available:', {
    paperStocks: paperStocks.length,
    finishingProcesses: finishingProcesses.length,
    jobTypes: jobTypes.length
  });

  // Get job type name (not ID!)
  const jobTypeName = jobTypes.find(jt => jt.id === draft.jobType)?.name || draft.jobType || '';

  // Get paper stock name
  const selectedPaper = paperStocks.find(s => s.id === draft.paperStock);
  const paperStockName = selectedPaper?.name || draft.paperStockName || draft.paperStock || '';

  // Get cover stock name if booklet with separate cover
  let coverStockName;
  if (draft.isBooklet && draft.separateCover && draft.coverStock) {
    const coverPaper = paperStocks.find(s => s.id === draft.coverStock);
    coverStockName = coverPaper?.name || draft.coverStockName || draft.coverStock;
  }

  // Generate NCR description if NCR job type
  // Note: jobType can be 'ncr' or 'NCR' (case-insensitive check)
  const isNCR = draft.jobType && draft.jobType.toLowerCase() === 'ncr';
  let ncrDesc = '';
  let paperSelNames = [];
  if (isNCR) {
    console.log('NCR job detected, extracting paper selection names...');
    console.log('draft.paperSelectionNames:', draft.paperSelectionNames);
    console.log('draft.breakdown?.paper_selections_names:', draft.breakdown?.paper_selections_names);

    // Try to generate fresh description if we have paper stocks
    if (paperStocks.length > 0) {
      const ncrData = generateNcrDescription(draft, paperStocks, draft.standardSize);
      ncrDesc = ncrData.desc;
      paperSelNames = ncrData.paperSelNames;
    } else {
      // Fall back to draft's existing values, or extract from breakdown
      ncrDesc = draft.ncrDescription || '';
      paperSelNames = draft.paperSelectionNames
        || draft.breakdown?.paper_selections_names
        || draft.breakdown?.paper_selection_names
        || [];
    }
    console.log('Final paperSelNames:', paperSelNames);
  }

  // Map finishing process IDs to human-readable names
  // Fall back to draft.processes if finishingProcesses not available
  const finishingNames = finishingProcesses.length > 0
    ? mapFinishes(
      draft.finishes || draft.selectedFinishes || [],
      draft.finishingOptions || {},
      finishingProcesses,
      finishingMaterials
    )
    : (draft.processes || []);

  const coverFinishingNames = finishingProcesses.length > 0
    ? mapFinishes(
      draft.selectedCoverFinishes || [],
      draft.coverFinishingOptions || {},
      finishingProcesses,
      finishingMaterials
    )
    : (draft.coverProcesses || []);

  // Generate job name if not provided
  let jobName = draft.jobName || draft.draftName;
  if (!jobName) {
    if (isNCR) {
      jobName = `NCR ${draft.ncrType} ${draft.sheetsPerSet}-part ${draft.standardSize || `${draft.width}x${draft.height}mm`}`;
    } else {
      jobName = `${jobTypeName} ${draft.width}mm x ${draft.height}mm`;
    }
  }

  // Build materialNames and material fields
  // IMPORTANT: If paperSelNames is empty but draft already has these fields, preserve them
  let materialNames = '';
  if (isNCR) {
    console.log('Building materialNames for NCR job...');
    // For NCR, try multiple sources for material names
    if (paperSelNames.length > 0) {
      materialNames = paperSelNames.join(', ');
      console.log('Using paperSelNames:', materialNames);
    } else if (draft.paperSelectionNames && draft.paperSelectionNames.length > 0) {
      materialNames = draft.paperSelectionNames.join(', ');
      console.log('Using draft.paperSelectionNames:', materialNames);
    } else if (draft.breakdown?.paper_selections_names && draft.breakdown.paper_selections_names.length > 0) {
      materialNames = draft.breakdown.paper_selections_names.join(', ');
      console.log('Using draft.breakdown.paper_selections_names:', materialNames);
    } else if (draft.breakdown?.paper_selection_names && draft.breakdown.paper_selection_names.length > 0) {
      materialNames = draft.breakdown.paper_selection_names.join(', ');
      console.log('Using draft.breakdown.paper_selection_names:', materialNames);
    } else {
      materialNames = draft.materialNames || draft.material || '';
      console.log('Using draft.materialNames fallback:', materialNames);
    }
    console.log('Final materialNames:', materialNames);
  } else {
    materialNames = paperStockName || draft.materialNames || draft.material || '';
  }

  // Build dimensions string
  const dimensions = draft.isBooklet
    ? `${draft.width}mm x ${draft.height}mm (${draft.pageCount} pages)`
    : (draft.dimensions || `${draft.width}mm x ${draft.height}mm`);

  // CRITICAL: Use miscCosts (not selectedMiscCosts) for compatibility
  const miscCosts = draft.miscCosts || draft.selectedMiscCosts || [];

  return {
    id: draft.id || `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    productType: 'small_format',
    jobName,
    materialNames,
    material: materialNames,
    dimensions,
    quantity: draft.quantity || 1,
    processes: finishingNames,
    coverProcesses: coverFinishingNames,
    price: draft.price ?? draft.totalPrice ?? 0,
    breakdown: draft.breakdown || {},
    width: draft.width,
    height: draft.height,
    finishes: draft.finishes || draft.selectedFinishes || [],
    finishingOptions: draft.finishingOptions || {},
    miscCosts, // FIXED: Use miscCosts instead of selectedMiscCosts
    printerId: draft.printerId || draft.printer,
    jobType: jobTypeName, // Human-readable name
    isDoubleSided: draft.isDoubleSided,
    colours: draft.colours,
    notes: draft.notes || '',
    isBooklet: draft.isBooklet,
    pageCount: draft.isBooklet ? draft.pageCount : undefined,
    paperStock: draft.paperStock,
    paperStockName,
    coverStock: draft.isBooklet && draft.separateCover ? draft.coverStock : undefined,
    coverStockName,
    bleed: draft.bleed,
    numberOfDesigns: draft.numberOfDesigns,
    standardSize: draft.standardSize,
    selectedCoverFinishes: draft.isBooklet && draft.separateCover ? draft.selectedCoverFinishes : undefined,
    coverFinishingOptions: draft.isBooklet && draft.separateCover ? draft.coverFinishingOptions : undefined,
    coverColours: draft.isBooklet && draft.separateCover ? draft.coverColours : undefined,
    coverIsDoubleSided: draft.isBooklet && draft.separateCover ? draft.coverIsDoubleSided : undefined,
    dimension_input_type: draft.isFolded && !draft.isBooklet ? draft.dimensionInputType : null,
    foldType: draft.foldType,
    addedAt: draft.addedAt || new Date().toISOString(), // FIXED: Add timestamp

    // NCR-specific fields
    ncrType: isNCR ? draft.ncrType : undefined,
    sheetsPerSet: isNCR ? draft.sheetsPerSet : undefined,
    setsPerPad: isNCR && draft.ncrType === 'pads' ? draft.setsPerPad : undefined,
    paperSelections: isNCR ? draft.paperSelections : undefined,
    paperSelectionNames: isNCR ? (paperSelNames.length > 0 ? paperSelNames : draft.paperSelectionNames) : undefined,
    sheetPrinters: isNCR ? draft.sheetPrinters : undefined,
    sheetSides: isNCR ? draft.sheetSides : undefined,
    hasWraparoundShield: isNCR && draft.ncrType === 'pads' ? draft.hasWraparoundShield : undefined,
    hasLooseShield: isNCR && draft.ncrType === 'pads' ? draft.hasLooseShield : undefined,
    padGlueEdge: isNCR && draft.ncrType === 'pads' ? draft.padGlueEdge : undefined,
    hasNumbering: isNCR ? draft.hasNumbering : undefined,
    numberingStartAt: isNCR && draft.hasNumbering ? draft.numberingStartAt : undefined,
    ncrDescription: isNCR ? (ncrDesc || draft.ncrDescription) : undefined,

    // Pads job type (separate from NCR)
    padJobSheetsPerPad: draft.jobType?.toLowerCase() === 'pads' ? draft.padJobSheetsPerPad : undefined,

    // Printer name (if needed)
    printer: draft.printer
  };
};

/**
 * Build a large-format quote item from draft data
 * Mirrors buildProductsFromPieces from PriceCalculator.js:588-818
 */
const buildLargeFormatQuoteItem = (draft, options = {}) => {
  const {
    finishingProcesses = [],
    jobTypes = [],
    printMachines = [],
    materials = {} // Object with vinylMaterials, boardMaterials, etc.
  } = options;

  // Helper to find material by ID across all material types
  const findMaterialById = (materialId, materialType = null) => {
    if (!materialId) return null;

    const materialTypesToSearch = materialType
      ? [materialType]
      : ['vinyl', 'board', 'banner', 'poster', 'laminate', 'roller_banner', 'coloured_vinyl'];

    for (const type of materialTypesToSearch) {
      const materialsArray = materials[`${type}Materials`] || [];
      const found = materialsArray.find(m => m.id === materialId);
      if (found) return { ...found, type };
    }
    return null;
  };

  // Resolve materials array with full details
  const resolvedMaterials = Array.isArray(draft.materials)
    ? draft.materials.map(material => {
      const materialType = material.type || findMaterialById(material.id)?.type;
      const materialData = findMaterialById(material.id, materialType) || material;
      if (!materialData?.id) return null;

      return {
        id: materialData.id,
        type: materialType || materialData.type || material.type,
        name: materialData.name || material.name || materialData.id
      };
    }).filter(Boolean)
    : [];

  const materialNames = resolvedMaterials.map(m => m.name).join(', ') || draft.materialNames || draft.material || '';

  const pieceBreakdown = draft.breakdown || {};
  const finishNames = getLargeFormatProcessNames(draft, finishingProcesses);

  // Get job type label
  const jobTypeObj = jobTypes.find(jt => jt.id === draft.jobType) || {};
  const jobTypeLabel = jobTypeObj.name || pieceBreakdown.job_type || draft.jobType || 'Large Format Item';

  // Get machine label
  const machineObj = printMachines.find(m => m.id === draft.machineId) || {};
  const machineLabel = machineObj.name || draft.machine || '';

  // Build dimensions string
  const dimensionLabel = draft.width && draft.height
    ? `${draft.width}mm × ${draft.height}mm`
    : draft.dimensions || '';

  // Generate job name
  const jobName = draft.jobName || draft.draftName || draft.name || jobTypeLabel || 'Large Format Item';

  return {
    id: draft.id || `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    jobName,
    productType: 'large-format',
    materialNames,
    material: materialNames,
    materials: resolvedMaterials,
    dimensions: dimensionLabel || `${draft.quantity || 1} item${draft.quantity === 1 ? '' : 's'}`,
    quantity: draft.quantity || 1,
    processes: finishNames,
    price: draft.price > 0
      ? draft.price
      : (pieceBreakdown.total_cost ?? pieceBreakdown.total_cost_before_min_charge ?? 0),
    breakdown: pieceBreakdown,
    width: draft.width,
    height: draft.height,
    finishes: Array.isArray(draft.finishes) ? [...draft.finishes] : [],
    finishingOptions: draft.finishingOptions || {}, // FIXED: Add finishingOptions
    machineId: draft.machineId,
    machine: machineLabel,
    jobType: jobTypeLabel,
    tradeDiscount: draft.tradeDiscount,
    notes: draft.notes || '',
    addedAt: draft.addedAt || new Date().toISOString(), // FIXED: Add timestamp

    // Multi-piece specific fields (preserve if present)
    ...(draft.multiPieceGroupId && {
      calculatorMode: draft.calculatorMode || 'multi',
      multiPieceGroupId: draft.multiPieceGroupId,
      multiPieceGroupName: draft.multiPieceGroupName,
      multiPieceGroupIndex: draft.multiPieceGroupIndex,
      multiPieceGroupCount: draft.multiPieceGroupCount,
      multiPieceBasePrice: draft.multiPieceBasePrice,
      multiPieceAdjustment: draft.multiPieceAdjustment,
      multiPiecePieceId: draft.multiPiecePieceId,
      multiPiecePricingTier: draft.multiPiecePricingTier,
      pieceConfiguration: draft.pieceConfiguration,
      ...(draft.multiPieceGroupSummary && {
        multiPieceGroupSummary: draft.multiPieceGroupSummary
      })
    }),

    // Other large-format specific fields
    routerCutLength: draft.routerCutLength,
    weedingComplexity: draft.weedingComplexity
  };
};

/**
 * Convert a single draft item to a quote item
 * Routes to the appropriate builder based on calculator type
 */
const convertSingleDraftItem = (draft, options = {}) => {
  const calculatorType = draft.calculatorType || draft.productType;

  if (calculatorType === 'small-format' || calculatorType === 'small_format') {
    return buildSmallFormatQuoteItem(draft, options);
  } else if (calculatorType === 'large-format' || calculatorType === 'large_format') {
    return buildLargeFormatQuoteItem(draft, options);
  } else {
    console.warn('Unknown calculator type:', calculatorType, 'for draft:', draft);
    // Fallback: try to infer from fields
    if (draft.paperStock || draft.isBooklet || draft.ncrType) {
      return buildSmallFormatQuoteItem(draft, options);
    } else if (draft.materials || draft.machineId) {
      return buildLargeFormatQuoteItem(draft, options);
    } else {
      throw new Error(`Cannot determine quote item type for draft: ${draft.id || 'unknown'}`);
    }
  }
};

const getEffectivePrice = (item) => {
  if (!item || typeof item !== 'object') return 0;
  const pick = (val) => val !== undefined && val !== null && !Number.isNaN(parseFloat(val));
  if (pick(item.overridePrice)) return parseFloat(item.overridePrice);
  if (pick(item.calculatedPrice)) return parseFloat(item.calculatedPrice);
  if (pick(item.priceBeforeOverride)) return parseFloat(item.priceBeforeOverride);
  return parseFloat(item.price) || 0;
};

/**
 * Main entry point: Convert draft to quote items
 *
 * Handles both:
 * - Multi-item drafts (draft.items array exists)
 * - Single-item drafts (top-level fields only)
 *
 * @param {Object} draft - The draft object to convert
 * @param {Object} options - Reference data (paperStocks, finishingProcesses, etc.)
 * @returns {Array} Array of quote items
 */
export const convertDraftToQuoteItems = (draft, options = {}) => {
  if (!draft) {
    throw new Error('Draft is required');
  }

  // CRITICAL: If draft has items array, convert each one
  if (draft.items && Array.isArray(draft.items) && draft.items.length > 0) {
    console.log(`Converting multi-item draft with ${draft.items.length} items`);

    return draft.items.map((item, index) => {
      // Use the saved per-piece breakdown; fall back to draft.breakdown.pieces[index]
      // so that older drafts (saved before Fix 1) can still resolve per-piece prices.
      const savedPerPieceBreakdown = Array.isArray(draft.breakdown?.pieces)
        ? draft.breakdown.pieces[index]
        : null;
      const resolvedBreakdown =
        item.breakdown?.total_cost != null
          ? item.breakdown
          : (savedPerPieceBreakdown || item.breakdown || {});

      // Merge draft-level fields with item-level fields (item takes precedence)
      const mergedItem = {
        ...draft,
        ...item,
        breakdown: resolvedBreakdown,
        // Preserve original IDs
        id: item.id || `item_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 9)}`
      };

      const converted = convertSingleDraftItem(mergedItem, options);
      const overrideProps = {
        calculatedPrice: mergedItem.calculatedPrice ?? mergedItem.priceBeforeOverride ?? converted.calculatedPrice,
        priceBeforeOverride: mergedItem.priceBeforeOverride ?? converted.priceBeforeOverride,
        overridePrice: mergedItem.overridePrice ?? converted.overridePrice,
        overrideReason: mergedItem.overrideReason ?? converted.overrideReason,
        overriddenBy: mergedItem.overriddenBy ?? converted.overriddenBy,
        overriddenAt: mergedItem.overriddenAt ?? converted.overriddenAt,
      };
      const effectivePrice = getEffectivePrice({ ...converted, ...overrideProps });
      return { ...converted, ...overrideProps, price: effectivePrice };
    });
  }

  // Otherwise, convert draft top-level fields as single item
  console.log('Converting single-item draft');
  const convertedSingle = convertSingleDraftItem(draft, options);
  const overrideProps = {
    calculatedPrice: draft.calculatedPrice ?? draft.priceBeforeOverride ?? convertedSingle.calculatedPrice,
    priceBeforeOverride: draft.priceBeforeOverride ?? convertedSingle.priceBeforeOverride,
    overridePrice: draft.overridePrice ?? convertedSingle.overridePrice,
    overrideReason: draft.overrideReason ?? convertedSingle.overrideReason,
    overriddenBy: draft.overriddenBy ?? convertedSingle.overriddenBy,
    overriddenAt: draft.overriddenAt ?? convertedSingle.overriddenAt,
  };
  const effectivePrice = getEffectivePrice({ ...convertedSingle, ...overrideProps });
  return [{ ...convertedSingle, ...overrideProps, price: effectivePrice }];
};

/**
 * Convenience function: Convert draft and calculate total price
 * @returns {Object} { items: Array, totalPrice: number, convertedFromDraft: boolean }
 */
export const convertDraftToQuote = (draft, options = {}) => {
  const items = convertDraftToQuoteItems(draft, options);
  const totalPrice = items.reduce((sum, item) => sum + getEffectivePrice(item), 0);

  return {
    items,
    totalPrice,
    convertedFromDraft: true  // Flag to track quotes created from drafts
  };
};
